import { useParams } from 'react-router-dom';
import { useGetCCAApplicantResultQuery } from '../../../../../utils/api_service/ccaApi';
import { CCAQuestionResult } from '../../../../../utils/types/cca_types';

/** Groups questionResults by subdomain and returns average score (0–100) per subdomain. */
const computeSubdomainScores = (
  questionResults: CCAQuestionResult[],
): Record<string, number> => {
  const groups: Record<string, { sum: number; count: number }> = {};
  for (const qr of questionResults) {
    const key = qr.subdomain;
    if (!groups[key]) groups[key] = { sum: 0, count: 0 };
    groups[key].sum += qr.aiScore ?? 0;
    groups[key].count += 1;
  }
  const result: Record<string, number> = {};
  for (const [sd, { sum, count }] of Object.entries(groups)) {
    result[sd] = count > 0 ? Math.round(sum / count) : 0;
  }
  return result;
};

/**
 * Fetches the CCA result for a single applicant.
 * Reads jobOpeningId from the route params ({ id }).
 *
 * @param applicantId - The applicant's userId / applicantId
 */
export const useCCAApplicantResult = (applicantId: string) => {
  const { id: jobOpeningId = '' } = useParams<{ id: string }>();

  const { data, isLoading, isError } = useGetCCAApplicantResultQuery(
    { jobOpeningId, applicantId },
    { skip: !jobOpeningId || !applicantId },
  );

  const subdomainScores = data?.responses
    ? computeSubdomainScores(data.responses)
    : {};

  return { result: data, subdomainScores, isLoading, isError, jobOpeningId };
};
